import { useState, useEffect } from "react";
import { useAuth } from "../../lib/AuthContext";
import { CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

export default function AdminSettings() {
  const { authFetch } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${window.location.origin}/api/admin/settings`);
        if (res.ok) setSettings(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [authFetch]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (res.ok) setSaved(true);
    } catch (e) { console.error(e); }
    finally { setSaving(false); setTimeout(() => setSaved(false), 3000); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><CircleNotch className="w-8 h-8 text-teal animate-spin" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="admin-settings">
      <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Platform Settings</h1>

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {/* Lookup Limit */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Free Tier Daily Lookup Limit</label>
          <p className="text-xs text-slate-400 mb-2">Max NPI lookups per day for free users</p>
          <input
            type="number"
            value={settings.free_lookup_limit || "10"}
            onChange={e => update("free_lookup_limit", e.target.value)}
            className="w-32 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
          />
        </div>

        {/* Maintenance Mode */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Maintenance Mode</label>
          <p className="text-xs text-slate-400 mb-2">Shows maintenance message to non-admin users</p>
          <ToggleSwitch checked={settings.maintenance_mode === "true"} onChange={v => update("maintenance_mode", v ? "true" : "false")} />
        </div>

        {/* Feature Flags */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-slate-700 mb-3">Feature Flags</label>
          <div className="space-y-3">
            <FeatureToggle label="PECOS Lookup" checked={settings.pecos_enabled !== "false"} onChange={v => update("pecos_enabled", v ? "true" : "false")} />
            <FeatureToggle label="OIG Lookup" checked={settings.oig_enabled !== "false"} onChange={v => update("oig_enabled", v ? "true" : "false")} />
            <FeatureToggle label="New Signups" desc="Disable to close registration" checked={settings.signups_enabled !== "false"} onChange={v => update("signups_enabled", v ? "true" : "false")} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <Button onClick={save} disabled={saving} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2" data-testid="admin-save-settings-btn">
          {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : "Save Settings"}
        </Button>
        {saved && <span className="flex items-center gap-1 text-sm text-emerald-600"><CheckCircle className="w-4 h-4" weight="fill" /> Saved</span>}
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-teal/100" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function FeatureToggle({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {desc && <p className="text-xs text-slate-400">{desc}</p>}
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}
